/**
 * web/src/ui/ConfirmSheet.tsx — the app's one destructive-confirm sheet
 * (card snBw8Ua7Wnmc, Jonathan email 2026-11-03: "Remove memory should have
 * confirmation, same for delete elements").
 *
 * This deliberately reverses the quiet-remove decision on card xUZFWf8JtHo2:
 * an Undo line that lives 8 seconds was not enough — Jonathan removed a memory
 * by accident and asked for it back (the restore card 4gQFLXE7xYIw exists
 * because of it). Undo stays as the second net, but the delete is now asked
 * for first.
 *
 * One component, every destructive row: Cancel is focused on open (the safe
 * choice), Escape and a backdrop tap both cancel, and the confirming button
 * names the thing being removed instead of saying "OK".
 */
import { Overlay } from './Overlay';

export interface ConfirmSheetProps {
  open: boolean;
  /** A question, not a noun: "Remove this memory?" */
  title: string;
  /** The consequence, in plain words. Shown under the title. */
  body?: string;
  /** Defaults to 'Remove'. */
  confirmLabel?: string;
  /** Defaults to 'Cancel'. */
  cancelLabel?: string;
  /** True while the confirmed action is in flight — both buttons disable. */
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmSheet({
  open,
  title,
  body,
  confirmLabel = 'Remove',
  cancelLabel = 'Cancel',
  busy = false,
  onCancel,
  onConfirm,
}: ConfirmSheetProps) {
  if (!open) return null;

  return (
    <Overlay
      open={open}
      onClose={onCancel}
      className="more-overlay confirm-overlay"
      panelClassName="more-sheet confirm-sheet"
      labelledBy="confirm-sheet-title"
      testId="confirm-sheet"
    >
      <h2 id="confirm-sheet-title" className="confirm-title" data-test="confirm-title">
        {title}
      </h2>
      {body ? <p className="confirm-body">{body}</p> : null}
      <div className="confirm-actions">
        {/* Cancel first in DOM and focus order: the safe act is the default. */}
        <button
          type="button"
          className="pear-btn confirm-cancel"
          data-test="confirm-cancel"
          data-testid="confirm-cancel"
          disabled={busy}
          onClick={onCancel}
          autoFocus
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className="pear-btn pear-btn-blush confirm-go"
          data-test="confirm-go"
          data-testid="confirm-go"
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? 'Removing…' : confirmLabel}
        </button>
      </div>
    </Overlay>
  );
}
